import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';

interface Notification {
  id: string;
  user_id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'reminder';
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
  scheduled_for?: string;
  created_at: string;
}

interface CreateNotificationData {
  type: 'info' | 'success' | 'warning' | 'error' | 'reminder';
  title: string;
  message: string;
  link?: string;
  scheduled_for?: string;
}

export const useNotifications = (pollingInterval: number = 30000) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingTimerRef = useRef<number | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiClient.request<{ notifications: Notification[] }>(
        '/api/notifications'
      );

      if (result.error) {
        setError(result.error.message);
      } else if (result.data) {
        setNotifications(result.data.notifications);
        // Calculate unread count
        const unread = result.data.notifications.filter(n => !n.is_read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Fetch notifications error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create notification
  const createNotification = useCallback(async (data: CreateNotificationData) => {
    setError(null);

    try {
      const result = await apiClient.request<{ notification: Notification }>(
        '/api/notifications',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );

      if (result.error) {
        setError(result.error.message);
        return null;
      }

      if (result.data) {
        // Add new notification to the list
        setNotifications(prev => [result.data!.notification, ...prev]);
        if (!result.data.notification.is_read) {
          setUnreadCount(prev => prev + 1);
        }
        return result.data.notification;
      }

      return null;
    } catch (err) {
      setError('Failed to create notification');
      console.error('Create notification error:', err);
      return null;
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    setError(null);

    try {
      const result = await apiClient.request<{ notification: Notification }>(
        `/api/notifications/${id}/read`,
        {
          method: 'PUT',
        }
      );

      if (result.error) {
        setError(result.error.message);
        return false;
      }

      if (result.data) {
        // Update notification in the list
        setNotifications(prev =>
          prev.map(n => (n.id === id ? result.data!.notification : n))
        );
        // Decrease unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      }

      return false;
    } catch (err) {
      setError('Failed to mark notification as read');
      console.error('Mark as read error:', err);
      return false;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    setError(null);

    try {
      const result = await apiClient.request<{ message: string }>(
        '/api/notifications/mark-all-read',
        {
          method: 'POST',
        }
      );

      if (result.error) {
        setError(result.error.message);
        return false;
      }

      // Update all notifications to read
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
      return true;
    } catch (err) {
      setError('Failed to mark all as read');
      console.error('Mark all as read error:', err);
      return false;
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    setError(null);

    try {
      const result = await apiClient.request<{ message: string }>(
        `/api/notifications/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (result.error) {
        setError(result.error.message);
        return false;
      }

      // Remove notification from the list
      setNotifications(prev => {
        const notification = prev.find(n => n.id === id);
        if (notification && !notification.is_read) {
          setUnreadCount(count => Math.max(0, count - 1));
        }
        return prev.filter(n => n.id !== id);
      });
      return true;
    } catch (err) {
      setError('Failed to delete notification');
      console.error('Delete notification error:', err);
      return false;
    }
  }, []);

  // Get unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const result = await apiClient.request<{ count: number }>(
        '/api/notifications/unread-count'
      );

      if (result.data) {
        setUnreadCount(result.data.count);
      }
    } catch (err) {
      console.error('Fetch unread count error:', err);
    }
  }, []);

  // Refresh notifications (alias for fetchNotifications)
  const refresh = fetchNotifications;

  // Start polling
  const startPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }

    pollingTimerRef.current = setInterval(() => {
      fetchNotifications();
    }, pollingInterval);
  }, [fetchNotifications, pollingInterval]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // Load notifications on mount and start polling
  useEffect(() => {
    fetchNotifications();
    startPolling();

    return () => {
      stopPolling();
    };
  }, [fetchNotifications, startPolling, stopPolling]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchUnreadCount,
    refresh,
    startPolling,
    stopPolling,
  };
};
